import { useEffect, useState } from 'react';
import { ApiError, markVoicemailHeard, voicemailAudio } from '../api';
import { formatDuration } from '../phone/number';
import { PauseSmallIcon, PlaySmallIcon } from './icons';

// One audio element for the whole app: starting a message stops the one playing.
const player = typeof Audio !== 'undefined' ? new Audio() : null;
let current: number | null = null;
const listeners = new Set<() => void>();
const urls = new Map<number, string>();
if (player) {
  const notify = () => listeners.forEach((f) => f());
  player.addEventListener('play', notify);
  player.addEventListener('pause', notify);
  player.addEventListener('ended', () => {
    current = null;
    notify();
  });
  player.addEventListener('timeupdate', notify);
}

/** Play / pause a voicemail. The first play marks it heard (for all devices). */
export function VoicemailPlay({
  id,
  durationSec,
  heard,
  onHeard,
  label = 'Voicemail',
}: {
  id: number;
  durationSec: number;
  heard: boolean;
  onHeard?: (id: number) => void;
  label?: string;
}) {
  const [, tick] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const f = () => tick((n) => n + 1);
    listeners.add(f);
    return () => {
      listeners.delete(f);
    };
  }, []);

  const playing = current === id && player !== null && !player.paused;
  const position = current === id && player ? player.currentTime : 0;

  const toggle = async () => {
    if (!player) return;
    if (playing) {
      player.pause();
      return;
    }
    setError(null);
    try {
      if (current !== id) {
        let url = urls.get(id);
        if (!url) {
          setLoading(true);
          url = URL.createObjectURL(await voicemailAudio(id));
          urls.set(id, url);
        }
        player.src = url;
        current = id;
      }
      await player.play();
      if (!heard) {
        onHeard?.(id);
        void markVoicemailHeard(id).catch(() => undefined);
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Couldn’t play it.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <span className="vm-play-wrap">
      <button
        type="button"
        className={`vm-play${heard ? '' : ' vm-new'}`}
        onClick={() => void toggle()}
        disabled={loading}
        aria-label={`${playing ? 'Pause' : 'Play'} ${label.toLowerCase()}, ${formatDuration(durationSec)}${heard ? '' : ', new'}`}
      >
        {playing ? <PauseSmallIcon /> : <PlaySmallIcon />}
        <span>
          {label} {playing || position > 0 ? `${formatDuration(position)} / ` : ''}
          {formatDuration(durationSec)}
        </span>
      </button>
      {error && (
        <span className="vm-error" role="alert">
          {error}
        </span>
      )}
    </span>
  );
}
